import { Form, Head, Link, usePage } from "@inertiajs/react";
import { useTranslation } from "react-i18next";
import CaseWorkspace, {
  CaseFacts,
  CaseNavigation,
} from "@/components/case-workspace";
import { FormContent } from "@/components/page-canvas";
import { errorText, FormErrors } from "@/components/forms/form-errors";
import WebsiteDomainSettings, {
  type WebsiteDomain,
} from "@/components/website_domain_settings";
import SettingsLayout from "@/layouts/settings_layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  locationServicesPath,
  sitePath,
  userWebsitePath,
  userWebsiteLocationPath,
} from "@/routes";

type Site = {
  id: string;
  brand_name: string;
  headline_en: string;
  headline_ro: string;
  description_en: string;
  description_ro: string;
  published: boolean;
};
type Location = {
  id: string;
  name: string;
  locale: string;
  currency: string;
  description: string;
  published: boolean;
  public_offering_ids: string[];
  offerings: { id: string; name: string }[];
};
type Section = "content" | "locations" | "domain";
type Props = {
  site: Site;
  locations: Location[];
  domain: WebsiteDomain | null;
  domain_eligible: boolean;
  domain_target: string | null;
  section: Section;
  selected_location_id: string | null;
};
function InlineError({ id, error }: { id: string; error: unknown }) {
  return errorText(error) ? (
    <p id={id} className="text-sm text-destructive">
      {errorText(error)}
    </p>
  ) : null;
}

export default function WebsiteSettings({
  site,
  locations,
  domain,
  domain_eligible,
  domain_target,
  section,
  selected_location_id,
}: Props) {
  const { t } = useTranslation();
  const { errors } = usePage().props;
  const location = locations.find((item) => item.id === selected_location_id);
  const listing = section === "locations" && !location;
  return (
    <SettingsLayout>
      <Head title={t("website.title")} />
      <CaseWorkspace
        fluid={listing}
        editor={!listing}
        context={
          <CaseFacts
            facts={[
              {
                label: t("product_ux_admin.website.context"),
                value: site.brand_name || t("website.title"),
              },
              {
                label: t("product_ux_admin.website.publication"),
                value: t(
                  site.published
                    ? "product_ux_admin.website.published"
                    : "product_ux_admin.website.unpublished",
                ),
              },
            ]}
          />
        }
        navigation={
          <CaseNavigation
            items={(["content", "locations", "domain"] as const).map((id) => ({
              id,
              label: t(`access.website_${id}`),
              href: userWebsitePath({ section: id }),
            }))}
            active={section}
            label={t("product_ux_admin.tasks")}
          />
        }
      >
        <div className="space-y-6">
          <header className="space-y-3">
            <h1 className="text-xl font-semibold">
              {location ? location.name : t(`access.website_${section}`)}
            </h1>
            <p className="text-sm text-muted-foreground">
              {t(`product_ux_admin.website.${section}_help`)}
            </p>
            {site.published && (
              <Button
                variant="outline"
                render={<Link href={sitePath(site.id)} />}
              >
                {t("website.view")}
              </Button>
            )}
          </header>
          {section === "content" && (
            <FormContent>
              <Form action={userWebsitePath()} method="patch">
                {({ errors: formErrors, processing }) => (
                  <div className="space-y-5">
                    <input
                      type="hidden"
                      name="return_section"
                      value="content"
                    />
                    <FormErrors
                      errors={formErrors}
                      targets={Object.fromEntries(
                        [
                          "brand_name",
                          "headline_en",
                          "description_en",
                          "headline_ro",
                          "description_ro",
                        ].map((field) => [field, `website-${field}`]),
                      )}
                    />
                    {(
                      [
                        "brand_name",
                        "headline_en",
                        "description_en",
                        "headline_ro",
                        "description_ro",
                      ] as const
                    ).map((field) => (
                      <div key={field} className="space-y-2">
                        <Label htmlFor={`website-${field}`}>
                          {t(`website.${field}`)}
                        </Label>
                        {field.startsWith("description") ? (
                          <Textarea
                            id={`website-${field}`}
                            name={`company_website[${field}]`}
                            defaultValue={site[field]}
                            maxLength={10000}
                            aria-invalid={!!formErrors[field]}
                            aria-describedby={
                              formErrors[field]
                                ? `website-${field}-error`
                                : undefined
                            }
                          />
                        ) : (
                          <Input
                            id={`website-${field}`}
                            name={`company_website[${field}]`}
                            defaultValue={site[field]}
                            required={field === "brand_name"}
                            maxLength={field === "brand_name" ? 200 : 300}
                            aria-invalid={!!formErrors[field]}
                            aria-describedby={
                              formErrors[field]
                                ? `website-${field}-error`
                                : undefined
                            }
                          />
                        )}
                        <InlineError
                          id={`website-${field}-error`}
                          error={formErrors[field]}
                        />
                      </div>
                    ))}
                    <div className="flex items-center gap-3">
                      <input
                        type="hidden"
                        name="company_website[published]"
                        value="0"
                      />
                      <Checkbox
                        id="publish-site"
                        name="company_website[published]"
                        value="1"
                        defaultChecked={site.published}
                      />
                      <Label htmlFor="publish-site">
                        {t("website.publish_site")}
                      </Label>
                    </div>
                    <Button type="submit" disabled={processing}>
                      {t("website.save")}
                    </Button>
                  </div>
                )}
              </Form>
            </FormContent>
          )}
          {listing && (
            <div className="space-y-4">
              {locations.length === 0 ? (
                <p role="status">
                  {t("product_ux_admin.website.no_locations")}
                </p>
              ) : (
                locations.map((item) => (
                  <article
                    key={item.id}
                    className="flex min-w-0 flex-wrap items-start justify-between gap-4 rounded-lg border bg-card p-4"
                  >
                    <div className="min-w-0 space-y-2">
                      <h2 className="break-words font-medium">{item.name}</h2>
                      <p className="text-sm text-muted-foreground">
                        {t(`website.${item.locale}`)} · {item.currency}
                      </p>
                      <Badge variant="outline">
                        {t(
                          item.published
                            ? "product_ux_admin.website.published"
                            : "product_ux_admin.website.unpublished",
                        )}
                      </Badge>
                    </div>
                    <Button
                      variant="outline"
                      render={
                        <Link
                          href={userWebsitePath({
                            section: "locations",
                            location_id: item.id,
                          })}
                        />
                      }
                    >
                      {t("product_ux_admin.website.edit_location", {
                        location: item.name,
                      })}
                    </Button>
                  </article>
                ))
              )}
            </div>
          )}
          {section === "locations" && location && (
            <FormContent>
              <Form
                action={userWebsiteLocationPath(location.id)}
                method="patch"
              >
                {({ errors: formErrors, processing }) => (
                  <div className="space-y-5">
                    <input
                      type="hidden"
                      name="return_section"
                      value="locations"
                    />
                    <FormErrors
                      errors={formErrors}
                      targets={{
                        description: `description-${location.id}`,
                        public_offering_ids: "public-services",
                      }}
                    />
                    <div className="flex items-center gap-3">
                      <input
                        type="hidden"
                        name="website_location[published]"
                        value="0"
                      />
                      <Checkbox
                        id={`publish-${location.id}`}
                        name="website_location[published]"
                        value="1"
                        defaultChecked={location.published}
                      />
                      <Label htmlFor={`publish-${location.id}`}>
                        {t("website.publish_location")}
                      </Label>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor={`description-${location.id}`}>
                        {t("website.location_description")}
                      </Label>
                      <Textarea
                        id={`description-${location.id}`}
                        name="website_location[description]"
                        defaultValue={location.description}
                        maxLength={10000}
                        aria-invalid={!!formErrors.description}
                        aria-describedby={
                          formErrors.description
                            ? "location-description-error"
                            : undefined
                        }
                      />
                      <InlineError
                        id="location-description-error"
                        error={formErrors.description}
                      />
                    </div>
                    <fieldset
                      id="public-services"
                      tabIndex={-1}
                      className="space-y-3"
                    >
                      <legend className="mb-2 text-sm font-medium">
                        {t("website.public_services")}
                      </legend>
                      <p className="text-sm text-muted-foreground">
                        {t("website.services_help")}
                      </p>
                      <input
                        type="hidden"
                        name="website_location[public_offering_ids][]"
                        value=""
                      />
                      {location.offerings.map((offering) => (
                        <label
                          key={offering.id}
                          className="flex items-center gap-3 text-sm"
                        >
                          <Checkbox
                            name="website_location[public_offering_ids][]"
                            value={offering.id}
                            defaultChecked={location.public_offering_ids.includes(
                              offering.id,
                            )}
                          />
                          <span className="min-w-0 break-words">
                            {offering.name}
                          </span>
                        </label>
                      ))}
                      <InlineError
                        id="public-services-error"
                        error={formErrors.public_offering_ids}
                      />
                    </fieldset>
                    {location.offerings.length === 0 && (
                      <div
                        role="status"
                        className="space-y-3 rounded-lg border p-4"
                      >
                        <p className="text-sm text-muted-foreground">
                          {t("product_ux_admin.website.no_offerings")}
                        </p>
                        <Button
                          variant="outline"
                          render={
                            <Link href={locationServicesPath(location.id)} />
                          }
                        >
                          {t("product_ux_admin.website.review_services")}
                        </Button>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3">
                      <Button type="submit" disabled={processing}>
                        {t("website.save_location")}
                      </Button>
                      <Button
                        variant="outline"
                        render={
                          <Link
                            href={userWebsitePath({ section: "locations" })}
                          />
                        }
                      >
                        {t("product_ux_admin.website.back_locations")}
                      </Button>
                    </div>
                  </div>
                )}
              </Form>
            </FormContent>
          )}
          {section === "domain" && (
            <WebsiteDomainSettings
              domain={domain}
              eligible={domain_eligible}
              target={domain_target}
              returnSection="domain"
            />
          )}
          {section === "locations" && !location && (
            <FormErrors errors={errors} />
          )}
        </div>
      </CaseWorkspace>
    </SettingsLayout>
  );
}
